// Family-aware syndrome detection (pure). Operates on family-level evidence
// (multiple affected relatives + confirmed gene carriers), distinct from
// Phenomizer's individual-phenotype Profile Match. Criteria live as data in
// syndromes.criteria.js.

import { CONFIDENCE } from '../constants';
import { isPositiveCarrierStatus } from '../carrierStatusVocabulary';
import {
  CANCER_WORDS,
  NOT_THIS_CANCER,
  SYNDROME_CRITERIA
} from './syndromes.criteria';
import { affectedList } from './inheritance.helpers';

// pid of any family member carrying one of the syndrome's genes with a
// POSITIVE carrier status, else null.
//
// Only the shared vocabulary's positive values count. A truthy test used to
// accept any non-empty string, so 'non-carrier', 'normal', the legacy 'None',
// the phenotype statement 'Affected' and unrecognised integration values each
// confirmed a carrier — and the tested-negative person became the supporting
// evidence for the syndrome.
//
// Deliberately the carrier status alone, not classifyGenotypeResult: that also
// reads a pathogenic VARIANT with no status as positive (including a somatic
// one, since the tokenised `interpretation` spans every origin), which would
// widen what this rule calls a confirmed carrier rather than just stop the
// misread.
function familyGeneCarrier(ped, genes) {
  const set = new Set(genes.map((g) => g.toUpperCase()));
  for (const ind of ped.individuals)
    for (const g of ind.genotypes)
      if (
        g.gene &&
        set.has(g.gene.toUpperCase()) &&
        isPositiveCarrierStatus(g.carrierStatus)
      )
        return ind.pid;
  return null;
}

// A diagnosis coded in SNOMED CT ('SCTID:<code>').
const isSnomedCoded = (d) => /^SCTID:/i.test(String(d?.id || ''));

function detectOne(ped, crit) {
  // A cancer named by its site, where in situ, a secondary, the skin and
  // another kind of tumour never count (TGCLOUD-1847). A SNOMED CT code is read
  // that way only; any other name also as it always was.
  const bySite = (name) =>
    (crit.cancerSites || []).some((re) => re.test(name)) &&
    CANCER_WORDS.test(name) &&
    !NOT_THIS_CANCER.some((re) => re.test(name));
  const matches = (d) => {
    const name = d.name || '';
    if (isSnomedCoded(d)) return bySite(name);
    return crit.diseasePatterns.some((re) => re.test(name)) || bySite(name);
  };
  const matchDiseases = ped.familyDiseases.filter(matches);
  if (!matchDiseases.length) return null;

  const affectedPids = new Set();
  for (const d of matchDiseases)
    for (const ind of affectedList(ped, d.id)) affectedPids.add(ind.pid);

  const carrierPid = familyGeneCarrier(ped, crit.genes);
  const rationale = [];
  let confidence;
  const evidenceTypes = ['phenotype'];

  if (carrierPid && affectedPids.size >= 1) {
    confidence = CONFIDENCE.strong;
    rationale.push('Confirmed gene carrier with affected relative(s)');
    evidenceTypes.push('variant', 'family_pattern');
  } else if (affectedPids.size >= crit.minAffectedForStrong) {
    confidence = CONFIDENCE.strong;
    rationale.push(`${affectedPids.size} affected relatives`);
    evidenceTypes.push('family_pattern');
  } else if (affectedPids.size === 1) {
    confidence = CONFIDENCE.moderate;
    rationale.push(
      'Single affected relative — insufficient for family pattern alone'
    );
  } else {
    return null;
  }

  const supportingIds = [...affectedPids];
  if (carrierPid && !supportingIds.includes(carrierPid))
    supportingIds.push(carrierPid);

  return {
    id: crit.id,
    label: crit.label,
    omim: crit.omim,
    confidence,
    evidenceTypes,
    supportingIds,
    rationale,
    citedGuidelines: [crit.guideline],
    relatedDiseaseIds: matchDiseases.map((d) => d.id),
    relatedGenes: crit.genes,
    ruledOut: false,
    ruledOutReason: null
  };
}

export function detectSyndromes(ped) {
  return SYNDROME_CRITERIA.map((c) => detectOne(ped, c)).filter(Boolean);
}
